import React, { useRef, useEffect, forwardRef } from 'react';
import { cn } from '../../lib/utils.js';

// Editor moderno usando contentEditable nativo
const ModernEditor = forwardRef(({ 
  value = '', 
  onChange, 
  placeholder = 'Digite aqui...', 
  className,
  ...props 
}, ref) => {
  const editorRef = useRef(null);
  const isUpdating = useRef(false);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || isUpdating.current) return;

    // Atualizar conteúdo apenas se diferente
    if (editor.innerHTML !== value) {
      isUpdating.current = true;
      editor.innerHTML = value || '';
      isUpdating.current = false;
    }
  }, [value]);

  const handleInput = () => {
    if (isUpdating.current) return;
    
    const editor = editorRef.current;
    if (editor && onChange) {
      onChange(editor.innerHTML);
    }
  };

  const handleKeyDown = (e) => {
    // Adicionar funcionalidades básicas de formatação
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          document.execCommand('bold');
          handleInput();
          break;
        case 'i':
          e.preventDefault();
          document.execCommand('italic');
          handleInput();
          break;
        case 'u':
          e.preventDefault();
          document.execCommand('underline');
          handleInput();
          break;
      }
    }
  };

  const formatText = (command, value = null) => {
    document.execCommand(command, false, value);
    handleInput();
    editorRef.current?.focus();
  };

  return (
    <div className="modern-editor border rounded-md bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-3 border-b bg-gray-50/50">
        <div className="flex gap-1">
          <button
            type="button"
            className="px-3 py-1.5 text-sm border rounded hover:bg-gray-100 transition-colors"
            onClick={() => formatText('bold')}
            title="Negrito (Ctrl+B)"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            className="px-3 py-1.5 text-sm border rounded hover:bg-gray-100 transition-colors"
            onClick={() => formatText('italic')}
            title="Itálico (Ctrl+I)"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            className="px-3 py-1.5 text-sm border rounded hover:bg-gray-100 transition-colors"
            onClick={() => formatText('underline')}
            title="Sublinhado (Ctrl+U)"
          >
            <u>U</u>
          </button>
        </div>
        
        <div className="w-px h-6 bg-gray-300 mx-2"></div>
        
        <div className="flex gap-1">
          <button
            type="button"
            className="px-3 py-1.5 text-sm border rounded hover:bg-gray-100 transition-colors"
            onClick={() => formatText('insertUnorderedList')}
            title="Lista com marcadores"
          >
            • Lista
          </button>
          <button
            type="button"
            className="px-3 py-1.5 text-sm border rounded hover:bg-gray-100 transition-colors"
            onClick={() => formatText('insertOrderedList')}
            title="Lista numerada"
          >
            1. Lista
          </button>
        </div>
        
        <div className="w-px h-6 bg-gray-300 mx-2"></div>
        
        <div className="flex gap-1">
          <button
            type="button"
            className="px-3 py-1.5 text-sm border rounded hover:bg-gray-100 transition-colors"
            onClick={() => formatText('justifyLeft')}
            title="Alinhar à esquerda"
          >
            ←
          </button>
          <button
            type="button"
            className="px-3 py-1.5 text-sm border rounded hover:bg-gray-100 transition-colors"
            onClick={() => formatText('justifyCenter')}
            title="Centralizar"
          >
            ↔
          </button>
          <button
            type="button"
            className="px-3 py-1.5 text-sm border rounded hover:bg-gray-100 transition-colors"
            onClick={() => formatText('justifyRight')}
            title="Alinhar à direita"
          >
            →
          </button>
        </div>
      </div>
      
      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        className={cn(
          "p-4 outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 prose prose-sm max-w-none",
          "min-h-[300px]",
          "[&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-gray-400 [&:empty]:before:pointer-events-none",
          className
        )}
        data-placeholder={placeholder}
        style={{
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          lineHeight: '1.6'
        }}
        {...props}
      />
    </div>
  );
});

ModernEditor.displayName = 'ModernEditor';

export default ModernEditor; 